/**
 * What is due at signing, and how it may be paid.
 *
 * Two rules from the owner (2026-08-17), and they are separate questions:
 *
 *  1. **How much.** Under $1,000 nothing is required — the customer may pay in
 *     full in advance if they would like to, and plenty do. At $1,000 and above
 *     it is half, taken when they sign.
 *  2. **By what means.** Any of them, on a job of any size. Up to 2026-09-06 a
 *     card was refused above $5,000, because a card fee is a percentage and a
 *     50% card deposit on a $34,000 roof cost the company about five hundred
 *     dollars to accept. The customer now pays that 3% themselves
 *     (`paymentChoice.ts`), so the reason for the ceiling went and the ceiling
 *     went with it.
 *
 * Both are computed off the **total the customer is actually buying** — the
 * package plus every upgrade they ticked — rather than off the estimate's own
 * price, which does not know what they chose.
 *
 * The $1,000 line is the same threshold the payment schedule splits on
 * (`PAYMENT_SPLIT_THRESHOLD`), and deliberately so: a deposit that disagreed
 * with the first payment on the same contract would be two numbers for one
 * promise.
 */

import { formatMoney, type Cents } from "./money.js";
import { PAYMENT_SPLIT_THRESHOLD } from "./paymentSchedule.js";

/** Half, at or above the threshold. */
export const DEPOSIT_SHARE = 0.5;

/**
 * How the customer may pay what is due at signing.
 *
 * `ach_only` is never produced any more — see above — but it stays in the union
 * because presented documents frozen before 2026-09-06 carry it, and ADR 0032
 * says a frozen document is read back exactly as it was signed.
 */
export type DepositMethods = "card_or_ach" | "ach_only";

export interface Deposit {
  /** Whether anything has to be paid before the job is booked. */
  required: boolean;
  /** What to collect. Zero when nothing is required. */
  amount: Cents;
  amountFormatted: string;
  methods: DepositMethods;
  /** The sentence the customer reads. Written here, not in a component. */
  text: string;
  /**
   * The same sentence without "The balance is due when the work is complete."
   * For a proposal that prints the rep's own schedule beneath it (ADR 0267):
   * that schedule says when the rest is due, often not on completion, and
   * the page would otherwise give the customer two answers.
   */
  atSigning: string;
}

const BALANCE = "The balance is due when the work is complete.";

/**
 * The deposit for a total, ready to put in front of somebody.
 *
 * `due` is what signing will actually bill: `signingFirstPayment` over what was
 * bought, which honours a first payment the rep typed — zero included. Left
 * out, it is the default half. Until 2026-09-10 the proposal only ever printed
 * the half, so a rep who typed $5,000 on a $29,150.05 roof sent a document
 * asking for $14,575.03 at signing and a bill asking for $5,000.
 *
 * The sentence names the share only when the figure is the share. A typed
 * figure is a number the rep chose, not a percentage of anything, and "a 50%
 * deposit of $5,000.00" on a $29,150.05 roof is a contradiction the customer
 * signs.
 *
 * Under the threshold nothing is required whatever `due` says: signing offers
 * the whole contract there without chasing it, which is what the sentence
 * already tells them.
 *
 * Rounded to the cent by `Math.round`, which on a half-cent goes up — the
 * company's way, and it is at most one cent either way on a number the final
 * payment absorbs regardless.
 */
export function depositFor(total: Cents, due?: Cents): Deposit {
  if (total < PAYMENT_SPLIT_THRESHOLD) {
    const text =
      "No deposit is required to book this job. You are welcome to pay in full in advance if you would prefer.";
    return {
      required: false,
      amount: 0 as Cents,
      amountFormatted: formatMoney(0 as Cents),
      methods: "card_or_ach",
      text,
      atSigning: text,
    };
  }

  const half = Math.round(total * DEPOSIT_SHARE) as Cents;
  const amount = due ?? half;
  const methods: DepositMethods = "card_or_ach";
  const how = "paid by card or bank transfer";

  // A typed zero: the rep sold it with nothing at signing, and signing bills
  // nothing. Not the "pay in full in advance" invitation above — the signing
  // page offers no payment at all here, so the sentence must not promise one.
  if (amount <= 0) {
    const atSigning = "No deposit is due at signing.";
    return {
      required: false,
      amount: 0 as Cents,
      amountFormatted: formatMoney(0 as Cents),
      methods,
      text: `${atSigning} ${BALANCE}`,
      atSigning,
    };
  }

  // The share is read off the constant rather than written into the sentence.
  // `DEPOSIT_SHARE` exists to be changed in one place, and a sentence carrying
  // its own "50%" would go on saying so beside a figure that had moved — a
  // percentage and an amount contradicting each other on the document somebody
  // is about to sign, frozen into their copy by ADR 0032.
  const share = `${Number((DEPOSIT_SHARE * 100).toFixed(2))}%`;
  const what = amount === half ? `A ${share} deposit` : "A deposit";
  const atSigning = `${what} of ${formatMoney(amount)} is due at signing, ${how}.`;
  return {
    required: true,
    amount,
    amountFormatted: formatMoney(amount),
    methods,
    text: `${atSigning} ${BALANCE}`,
    atSigning,
  };
}
